import * as React from 'react';
import { Switch as ShadcnSwitch } from '@/hen-ui/shadcn/switch';
import { Label } from '@/hen-ui/atomics/Label';
import { Flex } from '@/hen-ui/composites/Flex';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { cn } from '@/hen-ui/utils';
export interface SwitchProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnSwitch> {
  label: string;
  error?: boolean;
  disabled?: boolean;
  checked?: boolean;
}

/**
 * Switch component following HEN Design System standards.
 *
 * A thin wrapper around shadcn/ui switch (Radix UI Switch primitive) with HEN-specific
 * variants and design tokens. All styling uses tokens from tailwind.config.ts.
 *
 * @example
 * // Basic usage
 * <Switch label="Default Switch" />
 *
 * @example
 * // Disabled Switch
 * <Switch label="Disabled Switch" disabled />
 *
 * @example
 * // Error Switch
 * <Switch label="Error Switch" error />
 */

export const Switch = React.forwardRef<
  React.ElementRef<typeof ShadcnSwitch>,
  SwitchProps
>(({ className, label, error, disabled, id, ...props }, ref) => {
  const generatedId = React.useId();
  const switchId = id ?? generatedId;

  return (
    <Flex align='center' gap='xxs' direction='row'>
      <ShadcnSwitch
        ref={ref}
        id={switchId}
        aria-invalid={error}
        disabled={disabled}
        className={className}
        {...props}
        data-hen-id={COMPONENT_IDS.Switch}
      />
      <Label
        className={cn(
          error && 'text-error-500',
          disabled && 'opacity-50',
          'font-normal',
        )}
        htmlFor={switchId}
      >
        {label}
      </Label>
    </Flex>
  );
});

Switch.displayName = 'Switch';
